import { useEffect, useState } from 'react';
import {
  JET_KINDS,
  airportsOf,
  chopperOf,
  formatPence,
  privateJetsOf,
  type AirportContent,
  type JetKind,
  type MyJet,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify, notifyProblem } from '../lib/alerts';
import { actionKey } from '../lib/format';

type Place = { id: string; name: string; icon: string; area: string };

// Departures' "My jet" (as Lagos Life's "Fly your own jet"): buy a light, mid-size or heavy jet; it parks on
// the private apron with your name on it. Then fly it to any of the game's airports: no boarding, no queue, only
// its fuel to pay. It can be sold back for part of what it cost.
export function JetTab({
  airport,
  from,
  isOpen,
  walletPence,
  sending,
  onFly,
  onWallet,
}: {
  airport: AirportContent;
  from: { placeId: string; name: string };
  isOpen: (placeId: string) => boolean;
  walletPence: number | null;
  sending: boolean;
  onFly: (to: string) => void;
  onWallet: () => void;
}) {
  const jets = privateJetsOf(airport);
  const [jet, setJet] = useState<MyJet | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState<string | null>(null);
  const [selling, setSelling] = useState(false);
  useEffect(() => {
    let live = true;
    api.play
      .jet()
      .then((reply) => live && setJet(reply.jet))
      .catch(() => live && setJet(null));
    return () => {
      live = false;
    };
  }, []);
  const stops = airportsOf(airport).filter(
    (stop) => stop.visible && isOpen(stop.placeId) && stop.placeId !== from.placeId,
  );
  useEffect(() => {
    if (!to && stops[0]) setTo(stops[0].placeId);
  }, [to, stops]);
  const act = async (work: () => Promise<{ jet: MyJet | null; message?: string }>) => {
    setBusy(true);
    try {
      const reply = await work();
      setJet(reply.jet);
      if (reply.message) notify(reply.message);
      onWallet();
    } catch (error) {
      notifyProblem(describeError(error).message);
    } finally {
      setBusy(false);
      setSelling(false);
    }
  };
  if (jet === undefined) return <p className="neighbours-note">Looking for your jet…</p>;
  if (!jet)
    return (
      <div className="jet-shop">
        <div className="jet-hero">
          <strong>🛩️ Fly your own jet</strong>
          <p>
            Buy a private jet and it parks on the private apron, with your name on it. No boarding, no queue.{' '}
            {jets.seconds}s to any airport.
          </p>
        </div>
        <div className="jet-models" role="group" aria-label="Jets for sale">
          {JET_KINDS.map((kind: JetKind) => {
            const model = jets.kinds[kind];
            const short = walletPence !== null && walletPence < model.pricePence;
            return (
              <div className="jet-model" key={kind}>
                <span className="jet-model-icon" aria-hidden="true">
                  {model.icon}
                </span>
                <span className="flights-to-name">
                  <strong>{model.label}</strong>
                  <small>{model.blurb}</small>
                  <small>About {formatPence(model.fuelPence)} in fuel a trip</small>
                </span>
                <button
                  type="button"
                  className="jet-buy"
                  disabled={busy || short}
                  aria-label={`Buy the ${model.label}`}
                  onClick={() => void act(() => api.play.buyJet({ kind }, actionKey('jet')))}
                >
                  {short
                    ? `${formatPence(model.pricePence - (walletPence ?? 0))} short`
                    : formatPence(model.pricePence)}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  const short = walletPence !== null && walletPence < jet.fuelPence;
  return (
    <div className="jet-shop">
      <div className="jet-hero">
        <strong>
          🛩️ Your {jet.label} · {jet.registration}
        </strong>
        <p>
          {jet.name} on the side, waiting on the private apron. About {formatPence(jet.fuelPence)} in fuel a trip ·{' '}
          {jets.seconds}s to any airport.
        </p>
      </div>
      <strong className="flights-step">Where to?</strong>
      <div className="flights-to" role="radiogroup" aria-label="Fly your jet to">
        {stops.map((stop) => (
          <button
            type="button"
            key={stop.placeId}
            role="radio"
            aria-checked={to === stop.placeId}
            className={to === stop.placeId ? 'active' : ''}
            onClick={() => setTo(stop.placeId)}
          >
            <span className="flights-code">{stop.code}</span>
            <span className="flights-to-name">
              <strong>{stop.name}</strong>
              <small>No boarding · {jets.seconds}s</small>
            </span>
            <em>{formatPence(jet.fuelPence)}</em>
          </button>
        ))}
      </div>
      <button type="button" className="city-card-go" disabled={!to || sending || short} onClick={() => to && onFly(to)}>
        {short ? `${formatPence(jet.fuelPence - (walletPence ?? 0))} short` : sending ? 'Fuelling…' : 'Fly my jet'}
      </button>
      {selling ? (
        <div className="jet-sell">
          <span>Sell it for {formatPence(jet.sellPence)}?</span>
          <button type="button" onClick={() => setSelling(false)} disabled={busy}>
            Keep it
          </button>
          <button
            type="button"
            className="jet-sell-yes"
            disabled={busy}
            onClick={() => void act(() => api.play.sellJet())}
          >
            Sell
          </button>
        </div>
      ) : (
        <button type="button" className="flights-terminal" onClick={() => setSelling(true)}>
          Sell my jet · {formatPence(jet.sellPence)}
        </button>
      )}
    </div>
  );
}

// Departures' "Chopper" (as Lagos Life's "VIP CHOPPER · Straight to the door"): from this airport's helipad to
// any place on the map, landing at its door
export function ChopperTab({
  airport,
  from,
  places,
  walletPence,
  sending,
  onFly,
}: {
  airport: AirportContent;
  from: { placeId: string; name: string };
  places: Place[];
  walletPence: number | null;
  sending: boolean;
  onFly: (to: string) => void;
}) {
  const chopper = chopperOf(airport);
  const [to, setTo] = useState<string | null>(null);
  const choices = places.filter((place) => place.id !== from.placeId);
  if (!chopper.visible) return <p className="neighbours-note">The choppers are not flying right now.</p>;
  const short = walletPence !== null && walletPence < chopper.farePence;
  return (
    <div className="jet-shop">
      <div className="jet-hero chopper">
        <strong>🚁 VIP CHOPPER</strong>
        <p>
          Straight to the door · anywhere on the map · {chopper.seconds}s · {formatPence(chopper.farePence)}
        </p>
      </div>
      <strong className="flights-step">Where to?</strong>
      <div className="flights-to chopper-places" role="radiogroup" aria-label="Fly the chopper to">
        {choices.map((place) => (
          <button
            type="button"
            key={place.id}
            role="radio"
            aria-checked={to === place.id}
            className={to === place.id ? 'active' : ''}
            onClick={() => setTo(place.id)}
          >
            <span className="flights-code" aria-hidden="true">
              {place.icon}
            </span>
            <span className="flights-to-name">
              <strong>{place.name}</strong>
              <small>{place.area}</small>
            </span>
          </button>
        ))}
      </div>
      <button type="button" className="city-card-go" disabled={!to || sending || short} onClick={() => to && onFly(to)}>
        {short
          ? `${formatPence(chopper.farePence - (walletPence ?? 0))} short`
          : sending
            ? 'Starting the rotors…'
            : to
              ? `Fly there · ${formatPence(chopper.farePence)}`
              : 'Pick where to land'}
      </button>
    </div>
  );
}
